import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, LayoutDashboard, Target, CheckSquare, Wallet, Compass, Wrench, Focus, Users, Lightbulb } from 'lucide-react';
import { useTranslation } from '@/contexts/LanguageContext';
import { cn } from '@/lib/utils';
import { GoalDialog } from '@/components/goals/GoalDialog';
import { AchievementPublishDialog } from '@/components/AchievementPublishDialog';
import { useGoals } from '@/hooks/useGoals';
import { LucideIcon } from 'lucide-react';

interface BottomNavigationProps {
  onAddHabit: () => void;
  onAddTask: () => void;
  onAddTransaction: () => void;
}

export function BottomNavigation({ 
  onAddHabit, 
  onAddTask, 
  onAddTransaction 
}: BottomNavigationProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [goalDialogOpen, setGoalDialogOpen] = useState(false);
  const [postDialogOpen, setPostDialogOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { t, language } = useTranslation();
  const { addGoal } = useGoals();

  const isRussian = language === 'ru';

  const navItems: { path: string; icon: LucideIcon; label: string; color: string }[] = [
    { path: '/', icon: LayoutDashboard, label: t('home'), color: 'hsl(var(--primary))' },
    { path: '/habits', icon: Target, label: t('habits'), color: 'hsl(var(--habit))' },
    { path: '/tasks', icon: CheckSquare, label: t('tasks'), color: 'hsl(var(--task))' },
    { path: '/goals', icon: Compass, label: isRussian ? 'Цели' : 'Goals', color: 'hsl(262, 80%, 55%)' },
    { path: '/finance', icon: Wallet, label: t('finance'), color: 'hsl(var(--finance))' },
    { path: '/team', icon: Users, label: isRussian ? 'Команда' : 'Team', color: 'hsl(200, 80%, 50%)' },
    { path: '/services', icon: Wrench, label: t('services'), color: 'hsl(var(--service))' },
  ];

  const quickAddItems: { label: string; icon: LucideIcon; color: string; action: () => void; path: string }[] = [
    { label: isRussian ? 'Идея' : 'Idea', icon: Lightbulb, color: 'hsl(45, 93%, 47%)', action: () => navigate('/ideas'), path: '/ideas' },
    { label: isRussian ? 'Цель' : 'Goal', icon: Compass, color: 'hsl(262, 80%, 55%)', action: () => setGoalDialogOpen(true), path: '/goals' },
    { label: t('habit'), icon: Target, color: 'hsl(var(--habit))', action: onAddHabit, path: '/habits' },
    { label: t('task'), icon: CheckSquare, color: 'hsl(var(--task))', action: onAddTask, path: '/tasks' },
    { label: t('transaction'), icon: Wallet, color: 'hsl(var(--finance))', action: onAddTransaction, path: '/finance' },
    { label: isRussian ? 'Пост' : 'Post', icon: Focus, color: 'hsl(var(--primary))', action: () => setPostDialogOpen(true), path: '/focus' },
  ];

  const handleQuickAdd = (item: typeof quickAddItems[0]) => {
    setIsMenuOpen(false);
    if (item.path === '/ideas') {
      navigate('/ideas');
    } else if (item.label === (isRussian ? 'Цель' : 'Goal') || item.label === (isRussian ? 'Пост' : 'Post')) {
      item.action();
    } else {
      navigate(item.path);
      setTimeout(() => {
        item.action();
      }, 100);
    }
  };

  const handleNavClick = (path: string) => {
    navigate(path);
  };

  const handleAddGoal = async (data: any) => {
    await addGoal(data);
    setGoalDialogOpen(false);
    navigate('/goals');
  };

  // Left: Home, Habits, Tasks, Goals | Right: Finance, Team, Services
  const leftItems = navItems.slice(0, 4);
  const ideasItem = { path: '/ideas', icon: Lightbulb, label: isRussian ? 'Идеи' : 'Ideas', color: 'hsl(45, 93%, 47%)' };
  const rightItems = [ideasItem, ...navItems.slice(4)];

  return (
    <>
      {/* Overlay */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-background/80 backdrop-blur-sm z-40"
            onClick={() => setIsMenuOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Quick Add Menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <div className="fixed bottom-28 inset-x-0 z-50 flex justify-center">
            <div className="relative w-0 h-0">
              {quickAddItems.map((item, index) => {
                const totalItems = quickAddItems.length;
                const angleSpread = 120;
                const startAngle = -180 + (180 - angleSpread) / 2;
                const angleStep = angleSpread / (totalItems - 1);
                const angle = startAngle + (index * angleStep);
                const radius = 150;
                const x = Math.cos((angle * Math.PI) / 180) * radius;
                const y = Math.sin((angle * Math.PI) / 180) * radius;

                return (
                  <motion.button
                    key={item.label}
                    initial={{ scale: 0, opacity: 0, x: 0, y: 0 }}
                    animate={{ scale: 1, x, y, opacity: 1 }}
                    exit={{ scale: 0, x: 0, y: 0, opacity: 0 }}
                    transition={{ delay: index * 0.05, type: 'spring', stiffness: 300 }}
                    onClick={() => handleQuickAdd(item)}
                    className="absolute flex flex-col items-center justify-center gap-1"
                    style={{ transform: `translate(-50%, -50%)` }}
                  >
                    <div 
                      className="w-14 h-14 rounded-full flex items-center justify-center shadow-lg"
                      style={{ backgroundColor: item.color }}
                    >
                      <item.icon className="w-6 h-6 text-white" />
                    </div>
                    <span className="text-xs font-medium text-foreground whitespace-nowrap">
                      {item.label}
                    </span>
                  </motion.button>
                );
              })}
            </div>
          </div>
        )}
      </AnimatePresence>

      {/* Bottom Navigation Bar */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 bg-card/95 backdrop-blur-md border-t border-border safe-area-inset-bottom">
        <div className="max-w-lg mx-auto flex items-center h-16 px-1">
          {/* Left side: Home, Habits, Tasks, Goals */}
          <div className="flex items-center flex-1 justify-around">
            {leftItems.map((item) => (
              <button
                key={item.path}
                onClick={() => handleNavClick(item.path)}
                className={cn(
                  "flex flex-col items-center justify-center py-2 px-0.5 rounded-lg transition-all min-w-[34px]",
                  location.pathname === item.path
                    ? "scale-110"
                    : "opacity-70 hover:opacity-100"
                )}
              >
                <item.icon className="w-5 h-5" />
                <span className="text-[9px] mt-0.5 hidden sm:block">{item.label}</span>
              </button>
            ))}
          </div>

          {/* Center Add Button — shifted right */}
          <div className="relative px-2">
            <motion.button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-primary flex items-center justify-center shadow-glow -mt-6"
              whileTap={{ scale: 0.95 }}
              animate={{ rotate: isMenuOpen ? 45 : 0 }}
            >
              <Plus className="w-5 h-5 text-primary-foreground" />
            </motion.button>
          </div>

          {/* Right side: Finance, Team, Services */}
          <div className="flex items-center flex-1 justify-around">
            {rightItems.map((item) => (
              <button
                key={item.path}
                onClick={() => handleNavClick(item.path)}
                className={cn(
                  "flex flex-col items-center justify-center py-2 px-0.5 rounded-lg transition-all min-w-[34px]",
                  location.pathname === item.path
                    ? "scale-110"
                    : "opacity-70 hover:opacity-100"
                )}
              >
                <item.icon className="w-5 h-5" />
                <span className="text-[9px] mt-0.5 hidden sm:block">{item.label}</span>
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Goal Dialog */}
      <GoalDialog
        open={goalDialogOpen}
        onOpenChange={setGoalDialogOpen}
        onSave={handleAddGoal}
      />

      {/* Post Dialog */}
      <AchievementPublishDialog
        open={postDialogOpen}
        onOpenChange={setPostDialogOpen}
      />
    </>
  );
}
